import { useAuth0 } from '@auth0/auth0-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAsyncFn } from 'react-use'
import { type Asyncify } from 'type-fest'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import { HStack } from '~/components/elements/HStack'
import { Icon } from '~/components/elements/Icon'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  StyledInput,
  type StyledInputProps,
} from '~/components/elements/StyledInput'
import {
  StyledTextArea,
  type StyledTextAreaProps,
} from '~/components/elements/StyledTextArea'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { FigureInfoTable } from '~/components/features/FigureInfoTable'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { createImageData, type FigureInfo } from '~/utils'

import { useSaveDocumentImage } from './hooks/useSaveDocumentImage'

export type SaveDocumentImageDialogProps = {
  commentReadOnly?: boolean
  figureInfo: FigureInfo
  image: HTMLElement | React.ReactElement
  initialComment?: string
  onClose?: ExecuteCancelDialogProps['onCancel']
  onSaved?: (comment: string) => void
  restoringParams: unknown
}

export const SaveDocumentImageDialog = ({
  commentReadOnly = false,
  figureInfo: figureInfoProp,
  image,
  initialComment,
  onClose,
  onSaved,
  restoringParams,
}: SaveDocumentImageDialogProps) => {
  const { t } = useTranslation()
  const { user } = useAuth0()
  const logEvent = useLogEvent()
  const notify = useNotify()
  const saveDocumentImage = useSaveDocumentImage()

  const [comment, setComment] = React.useState(initialComment ?? '')
  const [title, setTitle] = React.useState('')

  const figureInfo = React.useMemo<FigureInfo>(
    () => ({
      creator: user?.email,
      ...figureInfoProp,
    }),
    [figureInfoProp, user?.email],
  )

  const handleCommentChange = React.useCallback<
    NonNullable<StyledTextAreaProps['onValueChange']>
  >((value) => {
    setComment(value)
  }, [])

  const handleTitleChange = React.useCallback<EH<StyledInputProps, 'onChange'>>(
    (event) => {
      setTitle(event.value)
    },
    [],
  )

  const [executeState, handleExecute] = useAsyncFn<
    Asyncify<EH<ExecuteCancelDialogProps, 'onExecute'>>
  >(async () => {
    const [imageData] = await createImageData(image, 'table')

    onClose?.()

    const saveDocumentImageResult = await saveDocumentImage({
      comment,
      figureInfo,
      imageData,
      restoringParams,
      title,
    })

    if (saveDocumentImageResult) {
      notify({
        content: (
          <HStack
            align="center"
            backgroundColor="transparent"
            padding={1}
            spacing={1}
          >
            <Typography color="inherit" variant="normal">
              {t('saved_3')}
            </Typography>

            <Link
              to={compile('documentImageList', {
                brandAccountId: figureInfo.brandAccountId,
              })}
            >
              <HStack align="center" backgroundColor="transparent" spacing={1}>
                <Icon icon="hyperlink-open" size="1em" />

                <Typography color="inherit" variant="link3">
                  {t('display_saved_information')}
                </Typography>
              </HStack>
            </Link>
          </HStack>
        ),
        style: 'success',
      })

      logEvent('Save table and graph', { withSuffix: true })

      onSaved?.(comment.trim())
    } else {
      notify({ content: t('failed_to_save_tablechart'), style: 'error' })
    }
  }, [
    comment,
    figureInfo,
    image,
    logEvent,
    notify,
    onClose,
    onSaved,
    restoringParams,
    saveDocumentImage,
    t,
    title,
  ])

  return (
    <>
      <ExecuteCancelDialog
        disabledExecute={executeState.loading}
        executeButtonText={t('save_3')}
        mode="create"
        title={t('save_image_for_document')}
        onCancel={onClose}
        onExecute={handleExecute}
      >
        <VStack spacing={4}>
          <LabelingContainer label={t('please_name_the_image_to_save')}>
            <StyledInput
              autoFocus={true}
              disabled={executeState.loading}
              placeholder={t('please_provide_a_name_based_on_usage_purpose')}
              value={title}
              width="100%"
              onChange={handleTitleChange}
            />
          </LabelingContainer>

          <LabelingContainer label={t('comment')}>
            {commentReadOnly && !comment ? (
              <Typography color="lighter" variant="normal">
                {t('no_comment')}
              </Typography>
            ) : (
              <StyledTextArea
                disabled={executeState.loading}
                placeholder={
                  commentReadOnly ? undefined : t('please_enter_a_comment')
                }
                readOnly={commentReadOnly}
                rows={3}
                value={comment}
                width="100%"
                onValueChange={
                  commentReadOnly ? undefined : handleCommentChange
                }
              />
            )}
          </LabelingContainer>

          <LabelingContainer
            label={t('save_information')}
            labelVariant="heading3"
          >
            <FigureInfoTable figureInfo={figureInfo} />
          </LabelingContainer>
        </VStack>
      </ExecuteCancelDialog>
    </>
  )
}
